// PPCPage.jsx
import React, { useEffect } from 'react';
import { Row, Col, Button, Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import xsimage from './paidxs.png'

const PPCPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
        // Scroll animation observer
        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
                }
            });
        }, { threshold: 0.1 });

        document.querySelectorAll('[data-animated]').forEach(el => observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const ppcStats = [
        { number: '65%', label: 'of all clicks on search results go to paid ads' },
        { number: '50%', label: 'higher conversion rate from PPC vs organic search' },
        { number: '40%', label: 'lower cost per acquisition with optimized bidding' },
        { number: '3x', label: 'ROI from strategic PPC campaigns in first 90 days' }
    ];

    const ppcApproaches = [
        {
            icon: 'fas fa-search',
            title: 'Paid Search Campaign Strategy',
            desc: 'Comprehensive market, audience, and keyword research to design campaigns aligned with business goals including lead generation, sales conversion, and brand expansion.'
        },
        {
            icon: 'fas fa-layer-group',
            title: 'Multi-Platform Paid Search Management',
            desc: 'Cross-platform PPC campaign management across Google Search, Microsoft Advertising, and other search platforms to maximize reach and capture high-intent audiences.'
        },
        {
            icon: 'fas fa-shopping-cart',
            title: 'Shopping & E-commerce PPC',
            desc: 'Performance-optimized Google Shopping campaigns, product feed optimization, and e-commerce focused strategies to drive product discoverability and conversions.'
        },
        {
            icon: 'fas fa-video',
            title: 'Display, Video & App Campaigns',
            desc: 'Targeted display advertising, YouTube video campaigns, and app install strategies to extend reach, nurture audiences, and drive awareness alongside search efforts.'
        },
        {
            icon: 'fas fa-chart-line',
            title: 'PPC Optimization & Performance Management',
            desc: 'Continuous campaign monitoring, audience refinement, bidding optimization, conversion tracking, and data-driven adjustments for maximum ROI and efficiency.'
        },
        {
            icon: 'fas fa-handshake',
            title: 'PPC Consultancy & Strategic Support',
            desc: 'Expert campaign audits, performance reviews, strategic recommendations, and in-house team training to enhance internal PPC capabilities and results.'
        }
    ];

    const benefits = [
        ['Immediate Search Visibility', 'Appear at the top of search results precisely when your target audience is ready to make decisions'],
        ['High-Intent Targeted Traffic', 'Reach users actively searching for your products or services with precise keyword targeting and intent matching'],
        ['Optimized Advertising Spend', 'Pay only for genuine engagement with sophisticated bidding strategies and cost-per-acquisition optimization'],
        ['Measurable Performance Tracking', 'Comprehensive conversion tracking, ROI analysis, and real-time performance monitoring with actionable insights'],
        ['Scalable Growth Infrastructure', 'Build campaigns that can expand with your business, testing and scaling based on proven performance data']
    ];

    const targetBusinesses = [
        'E-commerce stores seeking to scale online sales and product discoverability through targeted search advertising',
        'B2B brands generating qualified leads and pipeline development through intent-based keyword targeting',
        'Local businesses driving store visits, calls, and local conversions through geo-targeted PPC campaigns',
        'SaaS and technology companies boosting sign-ups, trials, and customer acquisition through performance advertising',
        'Service providers looking to capture high-intent customers at the moment of search decision-making',
        'Growth-focused organizations requiring measurable, performance-driven paid search results with clear ROI'
    ];

    const ppcQuestions = [
        'How can you capture customers at the exact moment they\'re searching for your solutions?',
        'What keyword strategies deliver the highest quality traffic and conversions for your business?',
        'How do you optimize bidding strategies to maximize return on advertising spend?',
        'What combination of search, display, and video advertising works best for your audience?',
        'How can you scale successful campaigns while maintaining profitability and efficiency?'
    ];

    return (<div className="paidmedia sv-page">
        <div className="sv-decorative-image-container">

        </div>

        <div className='xs-sv-page'>
            <img src={xsimage} />
        </div>
        {/* Page Title Section */}
        < div className="sv-page-title-section" >
            <div className="container">
                <h1>
                    <span className="sv-gradient-text">PPC Advertising</span> Services
                </h1>
                <p>Strategic pay-per-click campaigns that deliver immediate visibility, targeted traffic, and measurable ROI</p>
            </div>
        </div >

        {/* Main Content */}
        < div className="container-fluid sv-content-wrapper" >
            <div className="sv-main-content">
                {/* Hero Content Section */}
                <section className="sv-hero-section" data-animated>
                    <Row className="align-items-center">
                        <Col lg={8}>
                            <p className="sv-lead">
                                Pay-Per-Click (PPC) advertising remains one of the most powerful levers in digital marketing for rapidly increasing visibility, driving targeted traffic, and generating measurable business results.
                            </p>
                            <p className="sv-description">
                                At Reachtix, our <strong>PPC services</strong> are designed to deliver high-performance paid search campaigns that boost conversions, maximise ROI, and fuel sustainable growth across search and performance-driven platforms.
                            </p>
                            <p className="sv-description">
                                Unlike organic SEO, PPC delivers rapid results, complete control over budget and targeting, and measurable performance metrics that directly tie to business goals. It allows brands to appear at or near the top of search engine results, connect with high-intent audiences, and drive immediate traffic — from search queries to conversions.
                            </p>
                            <div className="sv-cta-buttons">
                                <Button
                                    tag={Link}
                                    to="/contact"
                                    color='dark'
                                >
                                    <i className="fas fa-rocket mr-2"></i>
                                    Launch PPC Campaign
                                </Button>
                                <Button
                                    tag={Link}
                                    to="/contact"
                                    color='dark'
                                >
                                    <i className="fas fa-chart-line mr-2"></i>
                                    Get PPC Consultation
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </section>

                {/* Stats Section */}
                <section className="sv-stats-section">
                    <h2 className="sv-section-title" data-animated>
                        The <span className="sv-text-gradient">PPC Performance Advantage</span>
                    </h2>
                    <Row className="sv-stats-grid">
                        {ppcStats.map((stat, index) => (
                            <Col lg={6} md={6} key={index} className="mb-4" data-animated>
                                <div className="sv-stat-card">
                                    <div className="sv-stat-number">{stat.number}</div>
                                    <div className="sv-stat-label">{stat.label}</div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </section>

                {/* What Is PPC Section */}
                <section className="sv-explanation-section" data-animated>
                    <Row>
                        <Col lg={8}>
                            <h2 className="sv-section-title">What Is PPC Advertising?</h2>
                            <p className="sv-section-description">
                                PPC (Pay-Per-Click) is a performance-based advertising model where businesses pay only when users click on their ads. It enables precise targeting of audiences based on search intent, demographics, and behavior across search engines and digital platforms.
                            </p>
                            <p className="sv-section-description">
                                This model provides immediate visibility in competitive search landscapes, measurable return on investment, and the flexibility to test, optimize, and scale campaigns based on real-time performance data and business objectives.
                            </p>
                            <div className="sv-questions-grid">
                                <h4>Key PPC Questions We Address:</h4>
                                {ppcQuestions.map((question, index) => (
                                    <div key={index} className="sv-question-item">
                                        <i className="fas fa-question-circle"></i>
                                        {question}
                                    </div>
                                ))}
                            </div>
                        </Col>
                    </Row>
                </section>

                {/* Why PPC Section */}
                <section className="sv-benefits-overview" data-animated>
                    <Row>
                        <Col xl={12}>
                            <h2 className="sv-section-title">Why PPC Drives Measurable Business Growth</h2>
                            <p className="sv-section-description">
                                PPC transforms advertising spend into predictable business outcomes, enabling organizations to capture market opportunities, validate customer acquisition strategies, and optimize marketing efficiency with precision and control.
                            </p>
                            <div className="sv-benefits-cards">
                                <div className="sv-benefit-card">
                                    <i className="fas fa-bolt"></i>
                                    <h5>Immediate Results</h5>
                                    <p>Launch campaigns that deliver visibility and conversions from the first day</p>
                                </div>
                                <div className="sv-benefit-card">
                                    <i className="fas fa-bullseye"></i>
                                    <h5>Intent-Based Targeting</h5>
                                    <p>Reach users actively searching for solutions you provide</p>
                                </div>
                                <div className="sv-benefit-card">
                                    <i className="fas fa-chart-line"></i>
                                    <h5>Performance Transparency</h5>
                                    <p>Track every click and conversion with detailed analytics</p>
                                </div>
                                <div className="sv-benefit-card">
                                    <i className="fas fa-expand"></i>
                                    <h5>Scalable Infrastructure</h5>
                                    <p>Test, validate, and expand campaigns based on proven performance</p>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </section>

                {/* Our PPC Approach Section */}
                <section className="sv-approach-section" data-animated>
                    <Row>
                        <Col>
                            <h2 className="sv-section-title">Our PPC Capabilities at Reachtix</h2>
                            <p className="sv-section-description">
                                We combine data-driven strategy, technical platform expertise, and continuous optimization to build PPC campaigns that perform at every stage of the customer journey and deliver measurable business impact.
                            </p>

                            <div className="sv-approaches-grid">
                                {ppcApproaches.map((item, index) => (
                                    <div key={index} className="sv-approach-card">
                                        <div className="sv-approach-icon">
                                            <i className={item.icon}></i>
                                        </div>
                                        <div className="sv-approach-content">
                                            <h4>{item.title}</h4>
                                            <p>{item.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Col>
                    </Row>
                </section>

                {/* Benefits Section */}
                <section className="sv-benefits-section" data-animated>
                    <Row>
                        <Col>
                            <h2 className="mt-5 sv-section-title">Benefits of PPC with Reachtix</h2>
                            <div className="table-responsive">
                                <Table borderless className="sv-benefits-table">
                                    <thead>
                                        <tr>
                                            <th>Strategic Advantage</th>
                                            <th>Business Impact</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {benefits.map(([advantage, impact], index) => (
                                            <tr key={index}>
                                                <td>
                                                    <i className="fas fa-check-circle"></i>
                                                    <strong>{advantage}</strong>
                                                </td>
                                                <td>{impact}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        </Col>
                    </Row>
                </section>

                {/* Who Should Invest Section */}
                <section className="sv-target-section" data-animated>
                    <Row>
                        <Col>
                            <h2 className="sv-section-title">Who Benefits Most from PPC Advertising?</h2>
                            <p className="sv-section-description">
                                PPC delivers maximum value for organizations seeking immediate visibility, measurable results, and scalable customer acquisition through targeted search advertising:
                            </p>
                            <div className="sv-target-list">
                                {targetBusinesses.map((business, index) => (
                                    <div key={index} className="sv-target-item">
                                        <i className="fas fa-chevron-right"></i>
                                        {business}
                                    </div>
                                ))}
                            </div>
                            <p className="sv-section-description mt-4">
                                Whether you're launching new products, entering competitive markets, or optimizing customer acquisition costs, strategic PPC provides the visibility and measurable results needed to achieve your growth objectives.
                            </p>
                        </Col>
                    </Row>
                </section>

                {/* Final CTA Section */}
                <section className="sv-main-cta" data-animated>
                    <Row>
                        <Col>
                            <h2>Ready to Accelerate Growth with Strategic PPC?</h2>
                            <p className="sv-cta-description">
                                PPC can transform your search visibility and accelerate your path to measurable business results. If you want campaigns that perform, adapt, and grow with your organization, Reachtix's <strong>PPC services</strong> are your next strategic step.
                            </p>
                            <p className="sv-cta-description">
                                At Reachtix, we treat PPC as a strategic growth engine — not just an advertising expense. Our specialists blend platform expertise with business insight to make your paid search advertising a powerful driver of measurable business outcomes and sustainable growth.
                            </p>
                            <div className="sv-cta-buttons">
                                <Button
                                    tag={Link}
                                    to="/contact"
                                    color='primary'
                                >
                                    <i className="fas fa-search-dollar mr-2"></i>
                                    Start PPC Campaign
                                </Button>
                                <Button
                                    tag={Link}
                                    to="/contact"
                                    color='light'
                                >
                                    <i className="fas fa-search mr-2"></i>
                                    Get Free PPC Audit
                                </Button>
                            </div>
                        </Col>
                    </Row>
                </section>
            </div>
        </div >
    </div >
    );
};

export default PPCPage;